import {Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {Assignment, AssignmentStudent} from '../classes/assignment';
import {LocalStorageService} from './local-storage.service';
import {ApiService, RemoteResponse} from './api.service';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {LearningOutcome} from '../classes/learning-outcome';
import { StudentsService } from './students.service';
import {LearningOutcomesService} from './learning-outcomes.service';

@Injectable({
  providedIn: 'root'
})
export class AssignmentsService {
  // This service is responsible for managing assignments
  // and their associated data. It provides methods to add, edit,
  // delete, and retrieve assignments.
  // It also handles the interaction with the API and local storage
  // to persist the data.
  // The service uses signals to manage the state of assignments
  // and provides a readonly signal to access the current state.
  // The service is injected into the components that need to access
  // the assignments data and provides methods to interact with it.

  private assignmentsSignal: WritableSignal<Assignment[]> = signal<Assignment[]>([]);
  public assignments: Signal<Assignment[]> = this.assignmentsSignal.asReadonly();

  constructor(
    private localStorage: LocalStorageService,
    private api: ApiService,
    private students: StudentsService,
    private learningOutcomes: LearningOutcomesService,
  ) {
    // Initialize assignments from local storage
    const assignments = this.localStorage.getAssignments();
    if (assignments.length > 0) {
      this.assignmentsSignal.set(assignments);
    }
    else{
      this.assignmentsSignal.set([]);
    }
  }

  public getAssignmentsFromApi(response: any): void {
    // Get assignments from the API response
    const assignments = this.getAssignmentsRemoteData(response);
    this.setAssignments(assignments);
  }

  public getAssignmentByRequestKey(requestKey: string): Assignment | undefined {
    // Get an assignment by its request key
    return this.assignmentsSignal().find(assignment => assignment.requestKey === requestKey);
  }

  private getAssignmentsRemoteData(response: any): Assignment[] {
    // Extract assignments from the API response
    const remoteData = response.data[0].dataSet;
    const assignments: Assignment[] = [];
    remoteData.assignments?.forEach((assignment: any) => {
      const assignmentObj = new Assignment();
      assignmentObj.setAssignment({
        name: assignment.name,
        requestKey: assignment.requestKey,
        learningOutcomes: [],
        dateAdded: assignment.dateAdded,
        createdBy: assignment.createdByFirstName + ' ' + assignment.createdByLastName,
        students: []
      });
      assignment.students.forEach((studentRequestKey: string) => {
        const newStudent = this.students.getStudentByRequestKey(studentRequestKey);
        if(newStudent) {
          const assignmentStudent: AssignmentStudent = {
            student: newStudent,
            scores: []
          };
          assignmentObj.students.push( assignmentStudent );
        }
      });
      assignment.outcomes.forEach((outcome: any) => {
        const learningOutcome = this.learningOutcomes.learningOutcomes().find(lo => lo.requestKey === outcome.requestKey);
        if(learningOutcome){
          assignmentObj.learningOutcomes.push(learningOutcome);
        }
      });
      assignments.push(assignmentObj);
    });
    return assignments;
  }

  public addLearningOutcomeToAssignment(learningOutcome: LearningOutcome, assignment: Assignment): Observable<RemoteResponse> {
    assignment.learningOutcomes.push(learningOutcome);
    return this.updateAssignment(assignment);
  }

  public deleteAssignment(assignment:Assignment): Observable<RemoteResponse> {
    // Delete an assignment from the list and return an observable
    return this.api.post('deleteAssignment', {
      requestKey: assignment.requestKey,
    }).pipe(
      tap(response => {
        if (response && response.success) {
          const assignments = this.assignmentsSignal();
          const index = assignments.findIndex(a => a.requestKey === assignment.requestKey);
          if (index !== -1) {
            assignments.splice(index, 1);
            this.setAssignments(assignments);
          }
        }
      })
    );
  }
  public removeLearningOutcomeFromAssignment(requestKey: string, assignment: Assignment): Observable<RemoteResponse> {
    // Remove a learning outcome from an assignment
    const index = assignment.learningOutcomes.findIndex(lo => lo.requestKey === requestKey);
    if (index !== -1) {
      assignment.learningOutcomes.splice(index, 1);
    }
    return this.updateAssignment(assignment);
  }
  public updateAssignment(assignment: Assignment): Observable<RemoteResponse> {
    // Update an assignment in the list and return an observable
    return this.api.post('saveAssignment', {
      name: assignment.name,
      requestKey: assignment.requestKey,
      outcomes: assignment.learningOutcomes.map(lo => lo.requestKey),
    }).pipe(
      tap(response => {
        if (response && response.success && response.data.length > 0 && response.data[0].dataSet) {
          // Update the assignment with the response data
          const remoteData = response.data[0].dataSet;
          assignment.setAssignment({
            requestKey: remoteData.requestKey,
            name: assignment.name,
            dateAdded: remoteData.dateAdded,
            createdBy: remoteData.createdByFirstName + ' ' + remoteData.createdByLastName,
            learningOutcomes: [...assignment.learningOutcomes],
            students: [...assignment.students],
          })
          this.updateAssignments(assignment);
        }
      })
    );
  }

  public updateStudentScore(
    assignmentRequestKey: string,
    studentRequestKey: string,
    learningOutcomeRequestKey: string,
    score: 0 | 1 | 2 | 3 | 4,
  ): Observable<RemoteResponse> {
    // Update the score of a student for a specific learning outcome in an assignment
    return this.api.post('saveScore', {
      assignmentRequestKey: assignmentRequestKey,
      studentRequestKey: studentRequestKey,
      outcomeRequestKey: learningOutcomeRequestKey,
      score: score,
    }).pipe(
      tap(response => {
        if (response && response.success) {
          const assignment = this.getAssignmentByRequestKey(assignmentRequestKey);
          if (assignment) {
            assignment.updateStudentScore(studentRequestKey, learningOutcomeRequestKey, score);
            this.updateAssignments(assignment);
          }
        }
      })
    );
  }

  public initAssignmentsScores(assignment: Assignment): Observable<RemoteResponse> {
    // Initialize scores for all students in an assignment
    return this.api.post('getScores', {
      assignmentRequestKey: assignment.requestKey,
    }).pipe(
      tap(response => {
        if (response && response.success) {
          // Update the assignment with the initialized scores
          const remoteData = response.data[0].dataSet;
          assignment.students.forEach(student => {
            const studentData = remoteData.filter((s: any) => s.studentRequestKey === student.student.requestKey);
            if (studentData) {
              student.scores = studentData.map((score: any) => ({
                learningOutcome: assignment.learningOutcomes.find(lo => lo.requestKey === score.outcomeRequestKey) || new LearningOutcome(),
                points: score.score as 0 | 1 | 2 | 3 | 4
              }));
            }
          });
          this.updateAssignments(assignment);
        }
      })
    );
  }

  public setAssignments(assignments: Assignment[]): void {
    // Set assignments in local storage and update the signal
    this.localStorage.setAssignments(assignments);
    this.assignmentsSignal.update(() => {
      return [...assignments];
    });
  }

  public addAssignment(assignment: Assignment): void {
    // Add a new assignment to the list and update the signal
    const assignments = this.assignmentsSignal();
    assignments.push(assignment);
    this.setAssignments(assignments);
  }


  public updateAssignments(assignment: Assignment): void {
    const assignments = this.assignmentsSignal();
    const index = assignments.findIndex(a => a.requestKey === assignment.requestKey);
    if (index !== -1) {
      assignments[index] = assignment;
    }
    else{
      assignments.push(assignment);
    }

    this.setAssignments(assignments);
  }

  public addStudentToAssignment(student: AssignmentStudent, assignment: Assignment): Observable<RemoteResponse> {
    return this.api.post('saveStudentAssignment', {
      name: student.student.getName(),
      requestKey: "",
      assignmentRequestKey: assignment.requestKey
    });
  }
}
